import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Alert,
  Avatar,
  ButtonBase,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Grid,
  Paper,
  styled,
  Typography,
} from '@mui/material'

import { Box, InfoRow } from '@/components/ui'
import type { StaffDetail } from '@/definitions/staff'
import { spacing } from '@/themes/tokens'

import { buildStaffDetailFields } from '../../utils/buildStaffDetailFields'
import type { StaffRow } from '../../utils/buildStaffRows'
import {
  staffDisplayName,
  staffInitials,
  staffRoleChipLabel,
} from '../../utils/staffLabels'
import { StaffActionsMenu } from '../StaffActionsMenu'
import { StaffErxSection } from '../StaffErxSection'

interface StaffCardProps {
  row: StaffRow
  isOpen: boolean
  onToggle: () => void
  /** Undefined until this card has been opened and its record has arrived. */
  detail: StaffDetail | undefined
  isDetailLoading: boolean
  isDetailError: boolean
  isActionPending: boolean
  isActionDisabled: boolean
  onSetEnabled: (enabled: boolean) => void
  orgId: string
  /** False locks this staff member's E-Prescribe: the practice has no Dosespot clinic. */
  isOrgErxEnabled: boolean
  /** True while the staff list is refreshing, which the E-Prescribe switch follows. */
  isRefreshing: boolean
}

const DETAIL_UNREADABLE_MESSAGE =
  'This staff member’s full record could not be loaded. Close and reopen to try again.'

/**
 * Opening the card is what the header is for, so it carries the card's padding and
 * takes the whole row the actions menu leaves it.
 */
const CardHeader = styled(ButtonBase)(({ theme }) => ({
  flex: 1,
  // Lets the header shrink rather than push the menu off the card.
  minWidth: 0,
  padding: theme.spacing(spacing.md),
  justifyContent: 'stretch',
  textAlign: 'left',
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: -2,
  },
}))

/**
 * The menu sits beside the header rather than in it — a button cannot nest in a
 * button. Its inset is smaller than the header's padding because the icon button
 * carries its own, which together come to the same edge the avatar keeps.
 */
const HeaderRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  paddingRight: theme.spacing(spacing.sm),
}))

const ExpandIcon = styled(ExpandMoreIcon, {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen: boolean }>(({ theme, isOpen }) => ({
  color: theme.palette.text.disabled,
  flexShrink: 0,
  transition: 'transform 0.2s ease',
  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
}))

const DetailBody = styled(Box)(({ theme }) => ({
  padding: theme.spacing(spacing.md),
}))

/**
 * One staff member, opening in place to show their full record.
 *
 * The list response already carries everything in the header, so a closed card
 * costs nothing to render; the rest is fetched only once someone opens it, which
 * is why this takes the detail and its loading state as props rather than
 * reaching for them itself. Deactivating follows the same rule — the card asks
 * for it and the tab is what confirms and performs it.
 *
 * Every detail field renders even when the staff member has no value for it —
 * `InfoRow` shows an em dash — so a gap in the record is visible rather than
 * silently absent. Creating and editing staff stay the practice's own admin's job
 * in Bartimaeus; the two things offered here are taking access away and switching
 * E-Prescribe, neither of which the practice can do for itself.
 */
export const StaffCard = ({
  row,
  isOpen,
  onToggle,
  detail,
  isDetailLoading,
  isDetailError,
  isActionPending,
  isActionDisabled,
  onSetEnabled,
  orgId,
  isOrgErxEnabled,
  isRefreshing,
}: StaffCardProps) => {
  const { staff, isAdmin } = row
  const detailId = `staff-detail-${staff.id}`
  const roleChip = staffRoleChipLabel(staff.user_type)

  return (
    <Paper variant='expandableCard'>
      <HeaderRow>
        <CardHeader onClick={onToggle} aria-expanded={isOpen} aria-controls={detailId}>
          <Box variant='flexBetween' gap='md'>
            <Box variant='flex' gap='md'>
              <Avatar variant='compactAvatar'>{staffInitials(staff)}</Avatar>
              <Box variant='flexColumn' gap='xs' fullWidth={false}>
                <Box variant='flex' gap='sm'>
                  <Typography variant='subtitle2' fontWeight={600}>
                    {staffDisplayName(staff)}
                  </Typography>
                  {/* Admin is the standing CS is scanning for, so it is filled;
                      any other named role is shown but stays subordinate to it. */}
                  {roleChip !== null &&
                    (isAdmin ? (
                      <Chip variant='status' label={roleChip} color='info' size='small' />
                    ) : (
                      <Chip variant='outlined' label={roleChip} size='small' />
                    ))}
                  <Chip
                    variant='status'
                    label={staff.enabled ? 'Active' : 'Deactivated'}
                    color={staff.enabled ? 'success' : 'error'}
                    size='small'
                  />
                </Box>
                <Typography variant='body2' color='text.secondary'>
                  {staff.email}
                </Typography>
              </Box>
            </Box>
            <ExpandIcon isOpen={isOpen} />
          </Box>
        </CardHeader>

        <StaffActionsMenu
          staffName={staffDisplayName(staff)}
          isStaffEnabled={staff.enabled}
          isActionPending={isActionPending}
          isActionDisabled={isActionDisabled}
          onSelectAction={() => onSetEnabled(!staff.enabled)}
        />
      </HeaderRow>

      <Collapse in={isOpen} unmountOnExit>
        <Divider />
        <DetailBody id={detailId} variant='flexColumn' gap='sm'>
          {isDetailLoading && (
            <Box variant='flexCenter'>
              <CircularProgress size={24} />
            </Box>
          )}

          {isDetailError && <Alert severity='warning'>{DETAIL_UNREADABLE_MESSAGE}</Alert>}

          {/* Four across on a full-width pane, halving down to one on a phone,
              so the record reads at a glance rather than as a long scroll. */}
          {detail && (
            <>
              <Grid container spacing={spacing.md}>
                {buildStaffDetailFields(detail).map((field) => (
                  <Grid key={field.label} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <InfoRow stacked label={field.label} value={field.value} />
                  </Grid>
                ))}
              </Grid>

              {/* Ruled off from the record above it: everything there is read-only,
                  and this writes. */}
              <Divider />
              <StaffErxSection
                staff={staff}
                detail={detail}
                orgId={orgId}
                isOrgErxEnabled={isOrgErxEnabled}
                isRefreshing={isRefreshing}
              />
            </>
          )}
        </DetailBody>
      </Collapse>
    </Paper>
  )
}
